import { type QueryClient, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';

// 중복 접수(더블클릭·연타) 방지: 결제/등록 mutation 은 진행 중이면 새 요청 안 보내고 진행중 Promise 반환.
import { useGuardedMutation as useMutation } from '../../hooks/useGuardedMutation';
import { API_CONFIG } from '../config/env';
import { InvoiceApi, InvoiceDtoBillingStatusEnum, InvoicePaymentApi, PaymentCardApi } from '../generated/payhub';
import type { InvoiceDto } from '../generated/payhub';
import {
  QK_PAYHUB_CONTRACT_FEE_RECORDS,
  QK_PAYHUB_INVOICE_FILTER,
  QK_PAYHUB_METERED_FEE_RECORDS,
  QK_PAYHUB_PACKAGE_FEE_RECORDS,
  QK_PAYMENT_CARD_INFO,
  QK_PAYMENT_LAST_STATUS,
} from '../query-keys';

export type {
  ContractFeeDto,
  ContractSnapshot,
  ContractUsageRecordDto,
  InvoiceDto,
  InvoiceFilterResponse,
  LastPaymentStatus,
  MeteredFeeDto,
  PackageFeeDto,
  PaymentCard,
  PaymentTransactionDto,
  ResponseDto,
  ScanDetails,
  ScanUsageRecordMetaData,
  ServiceFeeTotal,
  SessionUsageRecordDto,
  StimHomeDetails,
  StimHomeUsageRecordMetaData,
  SurveyUsageRecordDto,
} from '../generated/payhub';
export {
  ContractFeeDtoServiceTypeEnum,
  ContractUsageRecordDtoServiceTypeEnum,
  ContractUsageRecordDtoSourceEnum,
  InvoiceDtoBillingStatusEnum,
  InvoiceDtoPaymentStatusEnum,
  LastPaymentStatusStatusEnum,
  MeteredFeeDtoServiceTypeEnum,
  PackageFeeDtoServiceTypeEnum,
  PaymentTransactionDtoMethodEnum,
  PaymentTransactionDtoStatusEnum,
  PaymentTransactionDtoTypeEnum,
  ServiceFeeTotalCategoryEnum,
  ServiceFeeTotalServiceTypeEnum,
} from '../generated/payhub';

const invoiceApi = new InvoiceApi(undefined, API_CONFIG.PAYHUB_URL);
const invoicePaymentApi = new InvoicePaymentApi(undefined, API_CONFIG.PAYHUB_URL);
const paymentCardApi = new PaymentCardApi(undefined, API_CONFIG.PAYHUB_URL);

export const useFetchLastPaymentStatus = () => {
  return useQuery<boolean>({
    queryKey: [QK_PAYMENT_LAST_STATUS],
    queryFn: () => invoicePaymentApi.getLastPaymentStatus().then((res) => res.data.success),
  });
};

export const useFetchCardInfo = () => {
  return useQuery({
    queryKey: [QK_PAYMENT_CARD_INFO],
    queryFn: () =>
      paymentCardApi
        .getCard()
        .then((res) => res.data)
        .catch((e: AxiosError) => {
          if (e.response?.status === 404) {
            return null;
          }
          return Promise.reject(e);
        }),
  });
};

export const useHasCardInfo = () => {
  const res = useFetchCardInfo();
  return {
    ...res,
    data: res.data ? true : res.data === null ? false : undefined,
  };
};

export const useDeleteCard = () => {
  const client = useQueryClient();
  return useMutation<void, AxiosError>({
    mutationFn: () => paymentCardApi.deleteCard().then((res) => res.data),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: [QK_PAYMENT_CARD_INFO] });
    },
  });
};

export const useRegisterCard = () => {
  const client = useQueryClient();
  return useMutation<unknown, AxiosError, string>({
    mutationFn: (receiptId) => paymentCardApi.registerCard(receiptId),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: [QK_PAYMENT_CARD_INFO] });
    },
  });
};

export const useUpdateCard = () => {
  const client = useQueryClient();
  return useMutation<unknown, AxiosError, string>({
    mutationFn: (receiptId) => paymentCardApi.changeCard(receiptId),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: [QK_PAYMENT_CARD_INFO] });
    },
  });
};

export const useFilterInvoices = (yearMonth?: string) => {
  return useQuery({
    queryKey: [QK_PAYHUB_INVOICE_FILTER, yearMonth ?? null],
    queryFn: () => invoiceApi.filterInvoices(yearMonth).then((res) => res.data),
  });
};

/**
 * DRAFT 상태의 invoice는 records가 invoiceId에 아직 연결돼 있지 않을 수 있어
 * orgId + yearMonth를 함께 넘겨 조회한다.
 */
const draftParams = (invoice: InvoiceDto | undefined, orgId: number | undefined) => {
  const isDraft = invoice?.billingStatus === InvoiceDtoBillingStatusEnum.Draft;
  return {
    isDraft,
    orgId: isDraft ? orgId : undefined,
    yearMonth: isDraft ? invoice?.yearMonth : undefined,
  };
};

export const useMeteredFeeRecords = (invoice: InvoiceDto | undefined, orgId: number | undefined) => {
  const { isDraft, orgId: orgIdParam, yearMonth: ymParam } = draftParams(invoice, orgId);
  return useQuery({
    queryKey: [QK_PAYHUB_METERED_FEE_RECORDS, invoice?.id, orgIdParam ?? null, ymParam ?? null],
    queryFn: () => invoiceApi.getMeteredFeeRecords(invoice!.id, orgIdParam, ymParam).then((res) => res.data),
    enabled: !!invoice?.id && (!isDraft || !!orgId),
  });
};

export const usePackageFeeRecords = (invoice: InvoiceDto | undefined, orgId: number | undefined) => {
  const { isDraft, orgId: orgIdParam, yearMonth: ymParam } = draftParams(invoice, orgId);
  return useQuery({
    queryKey: [QK_PAYHUB_PACKAGE_FEE_RECORDS, invoice?.id, orgIdParam ?? null, ymParam ?? null],
    queryFn: () => invoiceApi.getPackageFeeRecords(invoice!.id, orgIdParam, ymParam).then((res) => res.data),
    enabled: !!invoice?.id && (!isDraft || !!orgId),
  });
};

export const useContractFeeRecords = (invoice: InvoiceDto | undefined, orgId: number | undefined) => {
  const { isDraft, orgId: orgIdParam, yearMonth: ymParam } = draftParams(invoice, orgId);
  return useQuery({
    queryKey: [QK_PAYHUB_CONTRACT_FEE_RECORDS, invoice?.id, orgIdParam ?? null, ymParam ?? null],
    queryFn: () => invoiceApi.getContractFeeRecords(invoice!.id, orgIdParam, ymParam).then((res) => res.data),
    enabled: !!invoice?.id && (!isDraft || !!orgId),
  });
};

/** lazy fetch (다운로드 등 클릭 시점 호출용) */
export const fetchInvoiceMeteredRecords = (
  client: QueryClient,
  invoice: InvoiceDto,
  orgId: number | undefined
) => {
  const { orgId: orgIdParam, yearMonth: ymParam } = draftParams(invoice, orgId);
  return client.fetchQuery({
    queryKey: [QK_PAYHUB_METERED_FEE_RECORDS, invoice.id, orgIdParam ?? null, ymParam ?? null],
    queryFn: () => invoiceApi.getMeteredFeeRecords(invoice.id, orgIdParam, ymParam).then((res) => res.data),
  });
};

export const fetchInvoicePackageRecords = (
  client: QueryClient,
  invoice: InvoiceDto,
  orgId: number | undefined
) => {
  const { orgId: orgIdParam, yearMonth: ymParam } = draftParams(invoice, orgId);
  return client.fetchQuery({
    queryKey: [QK_PAYHUB_PACKAGE_FEE_RECORDS, invoice.id, orgIdParam ?? null, ymParam ?? null],
    queryFn: () => invoiceApi.getPackageFeeRecords(invoice.id, orgIdParam, ymParam).then((res) => res.data),
  });
};

export const fetchInvoiceContractRecords = (
  client: QueryClient,
  invoice: InvoiceDto,
  orgId: number | undefined
) => {
  const { orgId: orgIdParam, yearMonth: ymParam } = draftParams(invoice, orgId);
  return client.fetchQuery({
    queryKey: [QK_PAYHUB_CONTRACT_FEE_RECORDS, invoice.id, orgIdParam ?? null, ymParam ?? null],
    queryFn: () => invoiceApi.getContractFeeRecords(invoice.id, orgIdParam, ymParam).then((res) => res.data),
  });
};

export const downloadInvoicePdf = (invoiceId: string) =>
  invoiceApi.downloadPdf(invoiceId, { responseType: 'blob' }).then((res) => res.data as unknown as Blob);
